import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ProductSetFullType, ProductSetType } from '@econtractor/types';
import * as apiProductSet from 'api/apiProductSets';
import { useToast } from 'context/ToastContext';
import SolarIcon from 'components/icons/SolarIcon';

export interface ProductSetSelectOption {
    value: number;
    label: string;
    itemsCount: number;
    article?: string;
}

export interface ProductSetsSelectProps {
    title?: string;
    onChange?: (option: ProductSetSelectOption | null) => void;
    value?: number | null;
    disabled?: boolean;
    required?: boolean;
    /** Лише монтування (без UI) — прогрів модуля / i18n */
    preload?: boolean;
}

const toOption = (set: ProductSetType | ProductSetFullType): ProductSetSelectOption => ({
    value: Number(set.ID),
    label: set.setName || `#${set.ID}`,
    itemsCount:
        'itemsCount' in set && set.itemsCount != null
            ? Number(set.itemsCount)
            : Array.isArray((set as ProductSetFullType).items)
              ? (set as ProductSetFullType).items.length
              : 0,
    article: set.setArticle || '',
});

const SetNameItem = ({ set }: { set: ProductSetSelectOption }) => {
    const { t } = useTranslation(['Sets']);
    const itemsLabel = t('selectItemsCount', { count: set.itemsCount });
    const titleMeta = [set.article, itemsLabel].filter(Boolean).join(' · ');

    return (
        <div className="min-w-0 overflow-hidden flex-grow-1">
            <h6 className="mb-1 text-truncate" title={set.label}>
                {set.label || '—'}
            </h6>
            <p className="f-12 mb-0 text-truncate" title={titleMeta}>
                {set.article ? <span className="text-muted">{set.article}</span> : null}
                {set.article ? <span className="text-muted"> · </span> : null}
                <span className="text-primary">{itemsLabel}</span>
            </p>
        </div>
    );
};

const ProductSetsSelect = ({
    title,
    onChange,
    value,
    disabled = false,
    required = true,
    preload = false,
}: ProductSetsSelectProps) => {
    const { t } = useTranslation(['Sets', 'common']);
    const { showToast } = useToast();
    const [search, setSearch] = useState('');
    const [options, setOptions] = useState<ProductSetSelectOption[]>([]);
    const [selected, setSelected] = useState<ProductSetSelectOption | null>(null);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const selectedIdRef = useRef<number | null>(null);
    selectedIdRef.current = selected?.value ?? null;

    const isFixed = Boolean(selected);
    const showResults = loading || searched;

    const resolveSetOption = async (setId: number): Promise<ProductSetSelectOption | null> => {
        const set = await apiProductSet.getProductSet(setId);
        return set ? toOption(set) : null;
    };

    const fetchSets = async () => {
        if (disabled || loading || isFixed) return;

        setLoading(true);
        const res = await apiProductSet.getList({
            search: (search || '').trim(),
            page: 1,
            onPage: 50,
            order: 'order by set_name',
        });
        setLoading(false);
        setSearched(true);

        if (!res) {
            setOptions([]);
            showToast(t('common:messError'), 'danger');
            return;
        }

        setOptions((res.sets || []).map(toOption));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            fetchSets();
        }
    };

    const handleSelect = (option: ProductSetSelectOption) => {
        if (disabled) return;
        setSelected(option);
        onChange?.(option);
        setSearch('');
        setOptions([]);
        setSearched(false);
    };

    const handleClear = () => {
        if (disabled) return;
        setSelected(null);
        onChange?.(null);
        setSearch('');
        setOptions([]);
        setSearched(false);
    };

    useEffect(() => {
        if (preload) return;

        let cancelled = false;

        const syncFromValue = async () => {
            if (value == null) {
                setSelected(null);
                return;
            }
            if (selectedIdRef.current == value) return;

            setLoading(true);
            const option = await resolveSetOption(value);
            if (cancelled) return;
            setLoading(false);
            setSelected(option);
        };

        syncFromValue();
        return () => {
            cancelled = true;
        };
    }, [value, preload]);

    if (preload) return null;

    const labelText = title || t('selectTitle');

    return (
        <div className="user-selected mb-3">
            {labelText && (
                <div className="d-flex justify-content-between align-items-center">
                    <label className="form-label text-muted mb-1">{labelText}</label>
                </div>
            )}

            {isFixed ? (
                <div className="user-selected-fixed">
                    <div className="user-selected-fixed-user d-flex align-items-center gap-2 min-w-0">
                        <SetNameItem set={selected!} />
                    </div>
                    <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="user-selected-clear"
                        disabled={disabled}
                        title={t('common:buttons.clear')}
                        onClick={handleClear}
                    >
                        <SolarIcon name="x-lg" />
                    </Button>
                </div>
            ) : (
                <div className={`user-selected-panel${showResults ? ' is-open' : ''}`}>
                    <InputGroup className="user-selected-search">
                        <Form.Control
                            type="text"
                            value={search}
                            disabled={disabled || loading}
                            required={required}
                            placeholder={t('selectSearchPlaceholder')}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        <Button
                            type="button"
                            variant="link"
                            className="user-selected-search-btn"
                            disabled={disabled || loading}
                            title={t('selectSearchBtn')}
                            onClick={fetchSets}
                        >
                            {loading ? (
                                <Spinner animation="border" size="sm" variant="primary" />
                            ) : (
                                <SolarIcon name="search" />
                            )}
                        </Button>
                    </InputGroup>

                    {showResults && (
                        <div className="user-selected-results position-relative" style={{ maxHeight: 220 }}>
                            {loading && (
                                <div className="user-selected-empty">
                                    <Spinner animation="border" size="sm" variant="primary" className="opacity-75" />
                                </div>
                            )}

                            {!loading && options.length === 0 && (
                                <div className="user-selected-empty">
                                    <SolarIcon name="collection" className="fs-5 d-block mb-1 opacity-50" />
                                    {t('selectNoResults')}
                                </div>
                            )}

                            {!loading && options.length > 0 && (
                                <ul className="user-selected-list">
                                    {options.map((option) => (
                                        <li key={option.value}>
                                            <button
                                                type="button"
                                                className="user-selected-item"
                                                disabled={disabled}
                                                onClick={() => handleSelect(option)}
                                            >
                                                <SetNameItem set={option} />
                                                <SolarIcon name="chevron-right" className="user-selected-item-chevron" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProductSetsSelect;
